import { useState } from 'react'
import { Box, Flex, VStack } from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { LuPlus } from 'react-icons/lu'
import { LuCircleCheck } from 'react-icons/lu'
import { DataTable, DataList, PageHeader, Text, useT } from '@minetrace/ui'
import { brand, themeColors } from '../../theme'
import { recepcionesService } from '../../api/services/recepciones.service'
import { usePagedRequest } from '../../hooks'
import {
  EstadoRecepcion,
  ResultadoValidacion,
  type GenerateSamplesResponse,
  type RecepcionListItemResponse,
} from '../../api/models/recepcion.models'
import { parseUtcDate } from '../../lib/dates'
import { exportToExcel, type ExcelColumn } from '../../utils/exportExcel'
import { GenerateSamplesModal } from './GenerateSamplesModal'
import { downloadAuthenticated } from '../Expedientes/ExpedienteElectronico/helpers'
import { toaster } from '@minetrace/ui'

const ESTADO_LABELS: Record<EstadoRecepcion, { tid: string; color: string }> = {
  [EstadoRecepcion.Pendiente]: { tid: 'recepciones.status.pendiente', color: '#857b71' },
  [EstadoRecepcion.Aprobada]: { tid: 'recepciones.status.aprobada', color: '#2a8a87' },
  [EstadoRecepcion.Rechazada]: { tid: 'recepciones.status.rechazada', color: '#c8442f' },
  [EstadoRecepcion.EnRevision]: { tid: 'recepciones.status.pendiente', color: '#EAB308' },
}

const RESULTADO_LABELS: Record<ResultadoValidacion, { tid: string; color: string }> = {
  [ResultadoValidacion.Pendiente]: { tid: 'recepciones.resultado.observado', color: '#857b71' },
  [ResultadoValidacion.Conforme]: { tid: 'recepciones.resultado.conforme', color: '#2a8a87' },
  [ResultadoValidacion.Observado]: { tid: 'recepciones.resultado.observado', color: '#EAB308' },
  [ResultadoValidacion.NoConforme]: { tid: 'recepciones.resultado.noConforme', color: '#c8442f' },
}

const formatDateTime = (iso: string | null) => {
  if (!iso) return '-'
  const d = parseUtcDate(iso)
  return (
    d.toLocaleDateString('es-PE', { day: '2-digit', month: 'short' }) +
    ' ' +
    d.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })
  )
}

export default function RecepcionesPage() {
  const colors = themeColors
  const navigate = useNavigate()
  const { t } = useT()
  const [search, setSearch] = useState('')

  // Estado del modal de muestreo (visible al generar o al consultar muestras ya creadas)
  const [muestreoOpen, setMuestreoOpen] = useState(false)
  const [muestreoRecepcionId, setMuestreoRecepcionId] = useState<string | null>(null)
  const [muestreoData, setMuestreoData] = useState<GenerateSamplesResponse | null>(null)
  const [muestreoError, setMuestreoError] = useState<string | null>(null)
  const [muestreoLoading, setMuestreoLoading] = useState(false)

  const excelColumns: ExcelColumn<RecepcionListItemResponse>[] = [
    { header: t('recepciones.excel.fecha'), accessor: (r) => formatDateTime(r.receivedAt) },
    { header: t('recepciones.excel.codigoLote'), accessor: (r) => r.batchCode },
    { header: t('recepciones.excel.ruc'), accessor: (r) => r.supplierRuc },
    { header: t('recepciones.excel.razonSocial'), accessor: (r) => r.supplierName },
    { header: t('recepciones.excel.mineral'), accessor: (r) => r.mineralTypeName ?? '-' },
    {
      header: t('recepciones.excel.estado'),
      accessor: (r) =>
        ESTADO_LABELS[r.estado]
          ? t(ESTADO_LABELS[r.estado].tid)
          : t('recepciones.status.desconocido'),
    },
    {
      header: t('recepciones.excel.resultado'),
      accessor: (r) =>
        r.overallResult && RESULTADO_LABELS[r.overallResult]
          ? t(RESULTADO_LABELS[r.overallResult].tid)
          : '-',
    },
    { header: t('recepciones.excel.muestras'), accessor: (r) => (r.hasSamples ? 'Si' : '-') },
  ]

  const {
    items: recepciones,
    totalCount,
    isFetching: loading,
    error,
    page,
    setPage,
    totalPages,
    pageSize,
    setPageSize,
    refresh,
  } = usePagedRequest(recepcionesService.getAll, {
    params: { busqueda: search.trim() || undefined },
  })

  // El acta se sirve desde la API con token: un <a href="/api/..."> apuntaba al dominio
  // de la web y sin autorización, así que "Descargar" no bajaba nada.
  const descargarActa = async (recepcionId: string, codigoLote: string) => {
    try {
      await downloadAuthenticated(
        `${import.meta.env.VITE_API_BASE_URL}/recepciones/${recepcionId}/acta`,
        `Acta_${codigoLote}.pdf`,
      )
    } catch {
      toaster.error({ description: 'No se pudo descargar el acta.' })
    }
  }

  const descargarActaVerificacion = async (recepcionId: string, codigoLote: string) => {
    try {
      await downloadAuthenticated(
        `${import.meta.env.VITE_API_BASE_URL}/recepciones/${recepcionId}/verification-report`,
        `Acta_Verificacion_${codigoLote}.pdf`,
      )
    } catch {
      toaster.error({ description: 'No se pudo descargar el Acta de Verificación.' })
    }
  }

  const abrirMuestreoGenerar = async (recepcionId: string) => {
    setMuestreoOpen(true)
    setMuestreoRecepcionId(recepcionId)
    setMuestreoData(null)
    setMuestreoError(null)
    setMuestreoLoading(true)
    try {
      const data = await recepcionesService.generarMuestreo(recepcionId)
      setMuestreoData(data)
      refresh()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error generando muestreo'
      setMuestreoError(message)
    } finally {
      setMuestreoLoading(false)
    }
  }

  const abrirMuestreoVer = async (recepcionId: string) => {
    setMuestreoOpen(true)
    setMuestreoRecepcionId(recepcionId)
    setMuestreoData(null)
    setMuestreoError(null)
    setMuestreoLoading(true)
    try {
      const data = await recepcionesService.listarMuestras(recepcionId)
      setMuestreoData(data)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error consultando muestras'
      setMuestreoError(message)
    } finally {
      setMuestreoLoading(false)
    }
  }

  return (
    <VStack gap={5} alignItems="stretch">
      <Flex justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={3}>
        <PageHeader
          title={t('recepciones.title')}
          subtitle={t('recepciones.subtitle')}
          colors={colors}
        />
        <Box
          as="button"
          display="flex"
          alignItems="center"
          gap={2}
          px={4}
          py={2.5}
          bg={brand.primary}
          color="white"
          borderRadius="lg"
          fontSize="sm"
          fontWeight="600"
          cursor="pointer"
          _hover={{ opacity: 0.9 }}
          transition="all 0.2s"
          onClick={() => navigate('/app/recepciones/nueva')}
        >
          <LuPlus size={16} />
          {t('recepciones.newButton')}
        </Box>
      </Flex>

      <DataList<RecepcionListItemResponse>
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('recepciones.searchPlaceholder')}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalCount={totalCount}
        entityName={t('recepciones.entityName')}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        onExport={() => exportToExcel(recepciones, excelColumns, t('recepciones.excel.fileName'))}
        brandColor={brand.green}
        headers={[
          t('recepciones.headers.fecha'),
          t('recepciones.headers.codigoLote'),
          t('recepciones.headers.proveedor'),
          t('recepciones.headers.mineral'),
          t('recepciones.headers.estado'),
          t('recepciones.headers.resultado'),
          t('recepciones.headers.muestras'),
          t('recepciones.headers.acta'),
        ]}
        data={recepciones}
        loading={loading}
        error={error}
        emptyMessage={t('recepciones.emptyMessage')}
        loadingMessage={t('recepciones.loadingMessage')}
        colors={colors}
        keyExtractor={(r) => r.id}
        renderRow={(recepcion) => {
          const estadoInfo = ESTADO_LABELS[recepcion.estado] ?? ESTADO_LABELS[EstadoRecepcion.Pendiente]
          const resultadoInfo =
            recepcion.overallResult && RESULTADO_LABELS[recepcion.overallResult]
              ? RESULTADO_LABELS[recepcion.overallResult]
              : null

          return (
            <>
              {/* Fecha — ahora primera columna */}
              <DataTable.Cell>
                <Text fontSize="xs" color={colors.textMuted} whiteSpace="nowrap">
                  {formatDateTime(recepcion.receivedAt)}
                </Text>
              </DataTable.Cell>

              {/* Codigo Lote */}
              <DataTable.Cell>
                <Text whiteSpace="nowrap"
                  fontFamily="'JetBrains Mono', monospace"
                  fontSize="sm"
                  fontWeight="600"
                  color={colors.text}
                >
                  {recepcion.batchCode}
                </Text>
              </DataTable.Cell>

              {/* Proveedor */}
              <DataTable.Cell>
                <VStack gap={0} alignItems="flex-start">
                  <Text fontSize="sm" color={colors.text} wordBreak="break-word">
                    {recepcion.supplierName}
                  </Text>
                  <Text fontSize="xs" color={colors.textMuted}>
                    {recepcion.supplierRuc}
                  </Text>
                </VStack>
              </DataTable.Cell>

              {/* Mineral */}
              <DataTable.Cell>
                <Text fontSize="sm" fontWeight="500" color={brand.green}>
                  {recepcion.mineralTypeName || '-'}
                </Text>
              </DataTable.Cell>

              {/* Estado */}
              <DataTable.Cell>
                <Box
                  display="inline-block"
                  px={2.5}
                  py={1}
                  borderRadius="full"
                  fontSize="10px"
                  fontWeight="700"
                  textTransform="uppercase"
                  letterSpacing="0.06em"
                  bg={`${estadoInfo.color}33`}
                  color={estadoInfo.color}
                >
                  {t(estadoInfo.tid)}
                </Box>
              </DataTable.Cell>

              {/* Resultado */}
              <DataTable.Cell>
                {resultadoInfo ? (
                  <Box
                    display="inline-block"
                    px={2.5}
                    py={1}
                    borderRadius="full"
                    fontSize="10px"
                    fontWeight="700"
                    textTransform="uppercase"
                    letterSpacing="0.06em"
                    bg={`${resultadoInfo.color}33`} whiteSpace="nowrap"
                    color={resultadoInfo.color}
                  >
                    {t(resultadoInfo.tid)}
                  </Box>
                ) : (
                  <Text fontSize="xs" color={colors.textMuted}>
                    -
                  </Text>
                )}
              </DataTable.Cell>

              {/* Muestras (check si el lote ya tiene muestras generadas) */}
              <DataTable.Cell>
                {recepcion.hasSamples ? (
                  <Flex alignItems="center" gap={1} color="#2a8a87">
                    <LuCircleCheck size={16} />
                    <Text fontSize="xs" fontWeight="600">
                      {recepcion.samplesCount}
                    </Text>
                  </Flex>
                ) : (
                  <Text fontSize="xs" color={colors.textMuted}>
                    -
                  </Text>
                )}
              </DataTable.Cell>

              {/* Acta */}
              <DataTable.Cell>
                {recepcion.tieneActa ? (
                  <Box
                    as="button"
                    fontSize="xs"
                    color={brand.green}
                    fontWeight="600"
                    cursor="pointer"
                    _hover={{ textDecoration: 'underline' }}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation()
                      void descargarActa(recepcion.id, recepcion.batchCode)
                    }}
                  >
                    {t('recepciones.actions.download')}
                  </Box>
                ) : (
                  <Text fontSize="xs" color={colors.textMuted}>
                    -
                  </Text>
                )}
              </DataTable.Cell>
            </>
          )
        }}
        actions={(recepcion) => {
          const items: { label: string; onClick: () => void }[] = [
            {
              label: t('recepciones.actions.viewDetail'),
              onClick: () => navigate(`/app/recepciones/${recepcion.id}`),
            },
          ]
          if (recepcion.tieneActa) {
            items.push({
              label: t('recepciones.actions.downloadActa'),
              onClick: () => void descargarActa(recepcion.id, recepcion.batchCode),
            })
          }
          if (recepcion.tieneVerificationReport) {
            items.push({
              label: t('recepciones.actions.downloadVerificationReport'),
              onClick: () => void descargarActaVerificacion(recepcion.id, recepcion.batchCode),
            })
          }
          if (recepcion.tieneActa) {
            items.push({
              label: t('recepciones.actions.viewBatchExpediente'),
              onClick: () =>
                navigate(`/app/identidad/expedientes/${encodeURIComponent(recepcion.batchCode)}/electronico`),
            })
          }
          // Acción "Generar Muestreo" — sólo visible si la recepción está aprobada y no
          // tiene muestras. Si ya tiene muestras se permite "Ver Muestreo" para reimprimir.
          if (recepcion.estado === EstadoRecepcion.Aprobada) {
            if (recepcion.hasSamples) {
              items.push({
                label: t('recepciones.actions.viewSamples'),
                onClick: () => abrirMuestreoVer(recepcion.id),
              })
            } else {
              items.push({
                label: t('recepciones.actions.generateSamples'),
                onClick: () => abrirMuestreoGenerar(recepcion.id),
              })
            }
          }
          return items
        }}
      />

      <GenerateSamplesModal
        open={muestreoOpen}
        loading={muestreoLoading}
        error={muestreoError}
        data={muestreoData}
        onClose={() => {
          setMuestreoOpen(false)
          setMuestreoRecepcionId(null)
          setMuestreoData(null)
          setMuestreoError(null)
        }}
        onPrinted={async () => {
          if (muestreoRecepcionId) {
            try {
              await recepcionesService.marcarMuestrasImpresas(muestreoRecepcionId)
            } catch {
              // print count es best-effort; no bloquea la UI
            }
          }
        }}
      />
    </VStack>
  )
}
